import { useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import {
  KyrgyzstanCard,
  type KyrgyzstanCardProps,
  KyrgyzstanEmpty,
  KyrgyzstanLoader,
} from 'entities/kyrgyzstan/ui'

import type { RealtyObject } from 'shared/api/kyrgyzstan/search'
import { SimpleLoader } from 'shared/ui/simple-loader'

import s from './styles.module.scss'

interface SearchResultListProps {
  data: RealtyObject[]
  isLoading: boolean
  error: string | null
  onCardClick?: (id: string) => void
}

export const SearchResultList = ({
  data,
  isLoading,
  error,
  onCardClick,
}: SearchResultListProps) => {
  const { t } = useTranslation()

  const realtys = useMemo<KyrgyzstanCardProps[]>(() => {
    return data.map((realty) => {
      return {
        id: realty.id,
        title: realty.title,
        city: realty.city,
        address: realty.address,
        images: realty.images,
        type: realty.type,
        totalPrice: parseFloat(realty.total_price || '0') || 0,
        pricePerNight: parseFloat(realty.price || '0') || 0,
        totalNights: realty.nights || 0,
        popularDynamicFields: realty.popular_dynamic_fields,
        dynamicFields: realty.dynamic_fields,
        mbonus_available: realty.mbonus_available ?? false,
        source: realty.source,
      }
    })
  }, [data])

  if (isLoading && realtys.length === 0) {
    return <KyrgyzstanLoader />
  }

  if (error) {
    return (
      <KyrgyzstanEmpty
        title={t('kyrgyzstan_block.error.title')}
        subtitle={t('kyrgyzstan_block.error.subtitle')}
      />
    )
  }

  if (realtys.length === 0 && !isLoading) {
    return (
      <KyrgyzstanEmpty
        title={t('kyrgyzstan_block.search_result.empty.title')}
        subtitle={t('kyrgyzstan_block.search_result.empty.subtitle')}
      />
    )
  }

  const isLoadingPagination = realtys.length > 0 && isLoading

  return (
    <>
      <div className={s.list}>
        {realtys.map((realty) => (
          <KyrgyzstanCard
            key={realty.id}
            {...realty}
            onClick={onCardClick}
          />
        ))}
      </div>

      {isLoadingPagination && (
        <div className={s.paginationLoader}>
          <SimpleLoader />
        </div>
      )}
    </>
  )
}
